'use client'

import { useEffect, useRef, useState } from 'react'
import { signIn, useSession } from 'next-auth/react'
import { useRouter, useSearchParams } from 'next/navigation'
import { toast } from '@/lib/toast'
import { formatImageURL, isValidEmail } from '@/lib/util'
import { startAuth, uploadFiles } from '@/lib/request'
import { checkUsername, claimUsername, fetchAuthState, savePersona, saveProfile, setPassword as savePassword, suggestUsernames } from '@/lib/signup'
import { apiError } from '@/lib/i18n/api-error'
import { clearClaim, readClaim } from '@/lib/claim'
import { AVATAR_MAX_MB, AVATAR_TYPES } from '@/components/dashboard/blocks'
import { useT } from '../i18n/LocaleProvider'
import type { NextStep, Session } from '@/lib/types'

import { Check, Copy, ImagePlus, Loader2, RotateCcw, X } from 'lucide-react'
import Input from '../elements/Input'
import { Button } from '../elements/Button'
import { AuthDivider, AuthHeader, AuthShell, initialsOf } from './AuthShell'
import { AuthAside } from './AuthAside'
import { PersonaPicker } from './PersonaPicker'
import { FirstBlock } from './FirstBlock'
import { personaNeedsPhone, type PreviewTile } from './persona-start'
import { isPersonaSlug, type PersonaSlug } from '@/lib/personas'
import { Skeleton } from '@/components/ui/skeleton'
import { Celebrate } from './Celebrate'
import {
  GoogleButton, HANDLE_MAX, OtpField, PasswordPair,
  formatPhone, isPasswordPairReady, isValidHandle, normaliseHandle, readContact, signInErrorCode,
} from './AuthFields'
import type { Contact } from './AuthFields'

/**
 * Sign-up, in seven steps: who the page is for, then the contact and its
 * code, a password, the profile, the handle, and the page's first block.
 *
 * The first and last are the page's, the five between are the account's.
 * The persona is asked before anything is typed because the words, the
 * preview, the phone question and the first-block ideas all follow from it.
 * The first block is asked after the handle because only then is there a
 * page to put it on; it is skippable and the account is complete without it.
 *
 * The order is not cosmetic. The account row is created the moment a contact
 * point is proven — step 2 — and everything after that is filling in a real
 * account, not accumulating a form to submit at the end. That is why each step
 * posts on its own and why abandoning halfway leaves something to come back to:
 * the backend re-derives `nextStep` from the row, so a return visit resumes
 * where it stopped instead of starting over.
 *
 * It also means the steps after 2 are authenticated. The session minted by the
 * code exchange is what authorises setting a password on an account that has
 * none — see `lib/signup.ts`.
 *
 * Signing up with Google skips steps 2 and 3: the contact is already verified
 * and the provider is the credential, so `nextStep` comes back as `username`.
 */
const STEPS = ['persona', 'contact', 'otp', 'password', 'profile', 'username', 'block'] as const

/**
 * The persona picked before an account exists, kept in the browser until the
 * profile step saves it. Local storage rather than state because a Google
 * sign-up leaves the page for Google and comes back, and the choice has to
 * survive the trip.
 */
const PERSONA_KEY = 'fuse_signup_persona'

function readStoredPersona(): PersonaSlug | null {
  try {
    const value = localStorage.getItem(PERSONA_KEY)
    return value && isPersonaSlug(value) ? value : null
  } catch { return null }
}

function storePersona(value: PersonaSlug) {
  try { localStorage.setItem(PERSONA_KEY, value) } catch { /* private mode: the state still holds it */ }
}

/**
 * Widths for the suggestion placeholders. Uneven on purpose, and as many as the
 * backend's SUGGESTION_COUNT: four identical bars read as a progress meter,
 * four uneven ones read as handles that have not landed yet.
 */
const SUGGESTION_SKELETONS = ['w-24', 'w-32', 'w-20', 'w-28']
type Step = (typeof STEPS)[number] | 'done'

/** Maps what the backend says is owed onto the screen that collects it. */
const STEP_FOR: Record<NextStep, Step> = {
  verify: 'otp',
  password: 'password',
  username: 'username',
  profile: 'profile',
  done: 'done',
}

/**
 * Colours for the initials disc shown when there is no photo: the brand
 * gradient and the zinc ramp, no second hue.
 *
 * These are a *preview* choice, not a saved one. `User.avatar` holds an image
 * path — the Google flow puts a photo URL in it — and there is no column for a
 * colour, so the pick styles the panel while signing up and is not sent to the
 * backend. Writing 'brand' into that column would render as a broken image at
 * `<bucket>/uploads/brand` everywhere the avatar is read.
 */
const AVATARS = [
  { key: 'brand', className: 'bg-gradient text-white' },
  { key: 'red', className: 'bg-gradient-red text-white' },
  { key: 'ink', className: 'bg-zinc-950 text-zinc-50' },
  { key: 'slate', className: 'bg-zinc-700 text-zinc-50' },
  { key: 'stone', className: 'bg-zinc-400 text-zinc-950' },
  { key: 'paper', className: 'bg-zinc-50 text-zinc-950' },
] as const

const BIO_MAX = 160


/**
 * The page's own address, derived from the deployment rather than hardcoded to
 * fuse.mn — so what the closing screen displays and what the copy button puts on
 * the clipboard are the same string in every environment. A screen that shows
 * `fuse.mn/bat` and copies `localhost:3000/bat` teaches people not to trust it.
 */
const PAGE_ORIGIN = (process.env.NEXT_PUBLIC_URL || 'https://fuse.mn').replace(/\/$/, '')
const PAGE_HOST = PAGE_ORIGIN.replace(/^https?:\/\//, '')

/**
 * @param initialStep - What the account still owes, read on the server. Present
 *   for anyone arriving with a session: a third-party sign-up sent here by the
 *   middleware gate, or a wizard abandoned and reopened.
 * @param initialName - The name the provider supplied, so step 5 opens filled in.
 * @param initialAvatar - The photo the provider supplied, same reasoning.
 */
export default function Register({ initialStep, initialName = '', initialAvatar = '' }: { initialStep?: NextStep, initialName?: string, initialAvatar?: string }) {
  const [step, setStep] = useState<Step>(initialStep ? STEP_FOR[initialStep] : 'persona')
  const [persona, setPersona] = useState<PersonaSlug | null>(null)
  /**
   * Where to go after the persona step when it was put in front of a resumed
   * sign-up: an account started before personas existed, or on a browser
   * that has forgotten the choice.
   */
  const [resumeTo, setResumeTo] = useState<Step | null>(null)
  /** The first block about to be added, shown landing in the preview. */
  const [freshTile, setFreshTile] = useState<PreviewTile | null>(null)
  /** The email-or-phone field as typed. */
  const [identity, setIdentity] = useState('')
  const [identityError, setIdentityError] = useState('')
  /**
   * Where the last code went, parsed. Kept apart from the field so the code
   * screen, the resend and the check all name the same contact even though
   * the field could still be edited behind them.
   */
  const [sentTo, setSentTo] = useState<Contact | null>(null)
  const [code, setCode] = useState('')
  const [codeError, setCodeError] = useState('')
  /**
   * The number was proven, but it sits unconfirmed on an account that already
   * exists (typed into its profile at sign-up). Almost always the person's own
   * account, so the code screen offers the way into it instead of a dead end.
   */
  const [numberTaken, setNumberTaken] = useState(false)

  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')

  const [username, setUsername] = useState('')
  const [nameChecking, setNameChecking] = useState(false)
  const [nameFree, setNameFree] = useState(false)
  const [nameReason, setNameReason] = useState('')
  const [handleFocused, setHandleFocused] = useState(false)
  const [suggestions, setSuggestions] = useState<string[]>([])
  /**
   * Starts true rather than being flipped on inside the effect. The panel only
   * renders on the handle step and the request is away before it first paints,
   * so "loading" is the honest opening state — and it keeps a setState out of
   * the effect body.
   */
  const [suggestLoading, setSuggestLoading] = useState(true)

  const [fullName, setFullName] = useState(initialName)
  const [phone, setPhone] = useState('')
  const [bio, setBio] = useState('')
  const [preset, setPreset] = useState<string | null>(null)
  /**
   * Bucket filename returned by /upload, an absolute URL when the photo came
   * from a provider, or null while the disc shows initials. formatImageURL
   * passes an absolute URL straight through, so both render the same way and
   * the profile step treats them alike: the preset swatches and the remove
   * button drop a Google photo exactly as they drop an upload.
   */
  const [avatar, setAvatar] = useState<string | null>(initialAvatar || null)
  const [avatarUploading, setAvatarUploading] = useState(false)
  const [copied, setCopied] = useState(false)

  const [loading, setLoading] = useState(false)
  const [googleLoading, setGoogleLoading] = useState(false)
  const [resendLoading, setResendLoading] = useState(false)

  const t = useT()
  const router = useRouter()
  const searchParams = useSearchParams()
  const { data: session, status, update } = useSession()
  const callbackUrl = searchParams.get('callbackUrl')
  /**
   * Signed up by SMS, so the number is already proven and the profile step
   * has nothing to ask about it. The backend would not let a typed number
   * replace it anyway: for this account it is the way in.
   */
  const phoneProven = !!session?.user?.isPhoneVerified
  // Asked only of the personas that take money, and never of a number already proven.
  const needPhone = !phoneProven && personaNeedsPhone(persona)

  // The choice made before an account existed, or before a trip to Google.
  useEffect(() => {
    const stored = readStoredPersona()
    // Read once on arrival; there is no render to keep in step with storage.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if(stored) setPersona(current => current ?? stored)
  }, [])
  const defaultEmail = searchParams.get('email')
  const checkTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const fileRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if(defaultEmail && isValidEmail(defaultEmail)) setIdentity(defaultEmail)
  }, [defaultEmail])

  useEffect(() => {
    if(searchParams.has('error')) {
      setLoading(false)
      setGoogleLoading(false)
      toast.error(t('auth.toast.registerFailed'))
    }
  }, [searchParams])

  /**
   * Confirms, once, that the step rendered on arrival is the step actually owed.
   *
   * `initialStep` already put the right screen on the first paint, but it came
   * from the session token, which is stamped at sign-in and only rewritten when
   * the wizard calls `update()`. Someone who finished on their phone still
   * carries a token here saying they owe a handle, and the middleware gate now
   * acts on that same claim — so the screen that traps them has to be the one
   * that checks the row underneath it. `/user` re-derives `nextStep` from the
   * database; if it says the account is complete, they leave instead.
   *
   * Keyed on `status` rather than `nextStep` because every completed step calls
   * `update()`: watching the step would fire again on the last one and redirect
   * over the closing screen the user just earned. Resuming is a question about
   * arrival. After that the wizard moves itself, through `advance()`.
   */
  const resumed = useRef(false)

  useEffect(() => {
    if(resumed.current || status === 'loading') return
    resumed.current = true
    // No session: a sign-up from scratch, already sitting on step 1.
    if(status !== 'authenticated' || !session?.user) return

    void (async () => {
      const state = await fetchAuthState(session as Session)
      // A backend that cannot be reached is not evidence the account changed.
      // Stay on the step the server rendered rather than guessing.
      const fresh = state && !state.error ? state.data : undefined
      if(!fresh) return

      // Whatever the account already carries, so a wizard resumed days later
      // opens on the real profile rather than back on the placeholder.
      if(fresh.name) setFullName(fresh.name)
      if(fresh.avatar) setAvatar(fresh.avatar)
      if(fresh.bio) setBio(fresh.bio)
      if(fresh.nextStep !== session.user.nextStep) await update({ user: { ...session.user, ...fresh } })

      if(!fresh.nextStep || fresh.nextStep === 'done') return router.replace(callbackUrl || '/profile')
      const saved = isPersonaSlug(fresh.persona ?? '') ? fresh.persona as PersonaSlug : null
      const known = saved ?? readStoredPersona()
      if(known) {
        setPersona(known)
        // Picked before a trip to Google, whose sign-ups skip the profile step
        // that would otherwise have saved it.
        if(!saved) void savePersona(known, session as Session)
        return setStep(STEP_FOR[fresh.nextStep])
      }
      // Nothing says who the page is for yet: ask, then carry on where it stopped.
      setResumeTo(STEP_FOR[fresh.nextStep])
      setStep('persona')
    })()
  }, [status])

  useEffect(() => () => { if(checkTimer.current) clearTimeout(checkTimer.current) }, [])

  /**
   * Handles built from the name, fetched once the handle step opens.
   *
   * Guarded by a ref rather than by the list being empty: an account whose name
   * yields nothing claimable is answered with `[]`, and keying on length would
   * re-ask on every render for exactly those users.
   *
   * Deliberately not re-fetched when the name changes. The suggestions are a
   * starting point offered on arrival, not a panel that tracks the field.
   */
  const suggested = useRef(false)

  /*
   * The handle claimed from the landing hero.
   *
   * Prefilled and then re-checked rather than trusted: the cookie says the name
   * was free when it was typed, which may have been an email round trip and an
   * OAuth detour ago. Running the same check the field runs is what turns it
   * green, and it is the same call a person typing it by hand would trigger.
   *
   * Fires when the step opens, not on mount, because that is when the field
   * exists to be filled and when a stale answer would actually mislead.
   */
  const seeded = useRef(false)
  useEffect(() => {
    if(seeded.current || step !== 'username') return
    const claimed = normaliseHandle(readClaim())
    if(!claimed) return
    seeded.current = true
    if(!isValidHandle(claimed)) return clearClaim()
    setUsername(claimed)
    setNameChecking(true)
    void runCheck(claimed)
  // `runCheck` is stable for this purpose: it closes over `session`, which the
  // username step already has by the time it renders.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step])

  useEffect(() => {
    if(step !== 'username' || status !== 'authenticated' || !session || suggested.current) return
    suggested.current = true
    void (async () => {
      const res = await suggestUsernames(session as Session)
      if(res && !res.error && Array.isArray(res.data)) setSuggestions(res.data)
      setSuggestLoading(false)
    })()
    // `session` is in the list only to satisfy the exhaustive-deps rule. Its
    // identity changes on every update(), and the ref guard above makes those
    // extra runs a no-op.
  }, [step, status, session])

  const stepIndex = step === 'done' ? STEPS.length : STEPS.indexOf(step as (typeof STEPS)[number])

  /* Step 1 — the contact point. */
  const handleSendCode = async (e?: any) => {
    e?.preventDefault()
    if(!identity.trim()) return setIdentityError(t('auth.toast.identityEmpty'))
    const contact = readContact(identity)
    if(!contact) return setIdentityError(t('auth.toast.identityInvalid'))

    setLoading(true)
    const res = await startAuth(contact)
    setLoading(false)
    if(!res) return toast.error(t('common.serverError'))
    // Under the field, like the format errors: an unsupported number or a
    // failed text is about what was typed there.
    if(res.error) return setIdentityError(apiError(t, res, 'auth.toast.registerFailed'))

    toast.success(t(contact.phone ? 'auth.toast.codeSentPhone' : 'auth.toast.codeSent'))
    setSentTo(contact)
    setCode('')
    setCodeError('')
    setStep('otp')
  }

  /* Step 2 — the code, which is also what creates the session. */
  const handleVerify = async (value: string) => {
    if(value.length !== 6) return setCodeError(t('auth.toast.otpEmpty'))

    setLoading(true)
    setCodeError('')
    if(!sentTo) return setStep('contact')
    const response = await signIn('otp', { ...sentTo, code: value, redirect: false })
    setLoading(false)

    if(!response) return toast.error(t('common.serverError'))
    if(response.error) {
      setCode('')
      const errorCode = signInErrorCode(response, 'verification.code_invalid')
      setNumberTaken(errorCode === 'auth.phone_unconfirmed')
      return setCodeError(apiError(t, errorCode, 'auth.toast.verifyFailed'))
    }

    toast.success(t('auth.toast.codeVerified'))
    // Where to go next comes from the refreshed session, not from here: the
    // account may already have had a password, in which case step 3 is not owed.
    const refreshed = await update() as Session | null
    setStep(STEP_FOR[refreshed?.user?.nextStep ?? 'password'])
  }

  const handleResend = async () => {
    if(!sentTo) return setStep('contact')
    setResendLoading(true)
    const res = await startAuth(sentTo)
    setResendLoading(false)
    if(!res) return toast.error(t('common.serverError'))
    if(res.error) return toast.error(apiError(t, res, 'auth.toast.resendFailed'))
    setCode('')
    setCodeError('')
    toast.success(t(sentTo.phone ? 'auth.toast.resendSuccessPhone' : 'auth.toast.resendSuccess'))
  }

  const registerWithGoogle = async () => {
    setGoogleLoading(true)
    if(callbackUrl) return await signIn('google', { callbackUrl })
    await signIn('google')
  }

  /**
   * Advances on whatever a step returned.
   *
   * Every step endpoint hands back the full user state, so the next screen is
   * read from the response rather than assumed — that keeps the wizard honest
   * when the backend decides a step was already satisfied.
   */
  const advance = async (result: any, fallback: Step, failureKey: Parameters<typeof apiError>[2], whenDone: Step = 'done') => {
    if(!result) { toast.error(t('common.serverError')); return false }
    if(result.error) { toast.error(apiError(t, result, failureKey)); return false }
    const refreshed = await update({ user: { ...session?.user, ...result.data } }) as Session | null
    const next = refreshed?.user?.nextStep
    setStep(!next ? fallback : next === 'done' ? whenDone : STEP_FOR[next])
    return true
  }

  /* The persona: who the page is for. */
  const handlePersonaContinue = () => {
    if(!persona) return
    storePersona(persona)
    // Signed in already (a resumed sign-up): saved now, since the profile
    // step that would carry it may be behind them.
    if(status === 'authenticated' && session) void savePersona(persona, session as Session)
    setStep(resumeTo ?? 'contact')
    setResumeTo(null)
  }

  /* Step 3 — the first password. */
  const handlePasswordSubmit = async () => {
    if(!isPasswordPairReady(password, confirm) || !session) return
    setLoading(true)
    const res = await savePassword(password, session as Session)
    setLoading(false)
    await advance(res, 'profile', 'auth.toast.registerFailed')
  }

  /* Step 5 — the handle. */
  const runCheck = async (value: string) => {
    setNameChecking(true)
    const res = await checkUsername(value, session as Session)
    setNameChecking(false)
    if(!res || res.error) return setNameReason('')
    setNameFree(!!res.data?.available)
    setNameReason(res.data?.reason || '')
  }

  const handleUsernameChange = (value: string) => {
    const next = normaliseHandle(value)
    setUsername(next)
    setNameFree(false)
    setNameReason('')
    if(checkTimer.current) clearTimeout(checkTimer.current)

    if(!isValidHandle(next)) return setNameChecking(false)

    // Debounced, so typing a handle costs one request rather than one per key.
    setNameChecking(true)
    checkTimer.current = setTimeout(() => { void runCheck(next) }, 500)
  }

  /**
   * A suggestion was free when the list was built, which may have been a while
   * ago, so picking one still goes through the same check as typing it. What it
   * skips is the debounce: a click is a decision, and waiting half a second
   * before the button wakes up reads as the app not having noticed.
   */
  const handlePickSuggestion = (value: string) => {
    if(checkTimer.current) clearTimeout(checkTimer.current)
    setUsername(value)
    setNameFree(false)
    setNameReason('')
    void runCheck(value)
  }

  const handleUsernameSubmit = async () => {
    if(!nameFree || !session) return
    setLoading(true)
    const res = await claimUsername(username, session as Session)
    setLoading(false)
    // On to the first block rather than straight to the end: the page exists now.
    if(await advance(res, 'block', 'auth.toast.registerFailed', 'block')) toast.success(t('auth.username.saved'))
  }

  /* Step 4 — name, bio, avatar. */
  const handleAvatarPick = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    // Clear the input straight away, so re-picking the same file after a failed
    // attempt still fires a change event.
    event.target.value = ''
    if(!file) return

    if(file.size > AVATAR_MAX_MB * 1024 * 1024) return toast.error(apiError(t, 'upload.too_large'))

    setAvatarUploading(true)
    const formData = new FormData()
    formData.append('file', file)
    const res = await uploadFiles({ formData })
    setAvatarUploading(false)

    if(!res) return toast.error(t('common.serverError'))
    if(res.status !== 200) return toast.error(apiError(t, res))
    // /upload answers with the bucket filenames it wrote. The bare name is what
    // is stored; `formatImageURL` expands it for display, so the record survives
    // the bucket being moved.
    const [fileName] = res.data ?? []
    if(!fileName) return toast.error(apiError(t, 'upload.failed'))
    setAvatar(fileName)
    setPreset(null)
  }

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(`${PAGE_ORIGIN}/${username}`)
      setCopied(true)
      // Reverts rather than latching, so the button reads as an action that can
      // be taken again, not as a state the screen is stuck in.
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // Denied permission, or an insecure origin. Say what to do instead.
      toast.error(t('auth.done.copyFailed'))
    }
  }

  const handleFinish = async () => {
    if(fullName.trim().length < 2 || (needPhone && phone.length !== 8) || !session) return
    setLoading(true)
    const res = await saveProfile({ name: fullName.trim(), bio: bio.trim(), avatar, phone: needPhone ? phone : undefined, persona: persona ?? undefined }, session as Session)
    setLoading(false)
    if(await advance(res, 'username', 'auth.toast.registerFailed')) toast.success(t('auth.toast.profileCreated'))
  }

  const routeToLogin = () => router.push(callbackUrl ? `/login?callbackUrl=${callbackUrl}` : '/login')

  const HEADINGS: Record<Step, { title: string, subtitle: string }> = {
    persona: { title: t('auth.persona.heading'), subtitle: t('auth.persona.subtitle') },
    // Written for whoever they said the page is for.
    contact: { title: t('auth.register.heading'), subtitle: t(`auth.persona.${persona ?? 'person'}.welcome`) },
    otp: {
      title: t('auth.otp.heading'),
      subtitle: sentTo?.phone
        ? t('auth.otp.sentToPhone', { contact: formatPhone(sentTo.phone) })
        : t('auth.otp.sentTo', { contact: sentTo?.email ?? '' }),
    },
    password: { title: t('auth.reset.heading'), subtitle: t('auth.reset.subtitle') },
    username: { title: t('auth.username.heading'), subtitle: t('auth.username.subtitle') },
    profile: { title: t('auth.profile.heading'), subtitle: t('auth.profile.subtitle') },
    block: { title: t('auth.firstBlock.heading'), subtitle: t('auth.firstBlock.subtitle') },
    done: { title: t('auth.done.heading'), subtitle: t('auth.done.registerSubtitle') },
  }

  const chosen = AVATARS.find(avatar => avatar.key === preset)
  const handleStatus = nameChecking ? t('auth.username.checking')
    : !isValidHandle(username) ? t('auth.username.invalid')
    : nameFree ? t('auth.username.available')
    : nameReason ? apiError(t, nameReason, 'auth.username.taken')
    : t('auth.username.taken')

  return (
    <AuthShell
      aside={
        <AuthAside
          persona={persona}
          fresh={step === 'block' ? freshTile : null}
          username={username}
          name={fullName}
          bio={bio}
          avatarClassName={chosen?.className}
          avatarUrl={avatar ? formatImageURL(avatar) : undefined}
          showCaret={step === 'username' && handleFocused}
        />
      }
    >
      {/* Progress. Hidden on the closing screen — there is nothing left to be
          partway through, and a full bar next to "all set" is noise. */}
      { step !== 'done' && (
        <div className="flex items-center justify-center gap-1.5 lg:justify-start">
          {/* Segments only. "1 / 7" reads as a long form; the bar shows the
              same progress without counting it out. */}
          <div role="progressbar" aria-valuemin={1} aria-valuemax={STEPS.length} aria-valuenow={stepIndex + 1} aria-label={t('auth.register.stepOf', { current: stepIndex + 1, total: STEPS.length })} className="flex items-center gap-1.5">
            { STEPS.map((name, index) => (
              <div
                key={name}
                className={`h-1 rounded-full transition-all ${index === stepIndex ? 'w-7 bg-gradient' : 'w-4'} ${
                  index < stepIndex ? 'bg-muted-foreground' : index > stepIndex ? 'bg-muted' : ''
                }`}
              />
            )) }
          </div>
        </div>
      ) }

      <AuthHeader
        title={HEADINGS[step].title}
        // On the closing screen the forward-looking line lives under the button,
        // next to the action it describes. Repeating it here would say the same
        // thing twice, twenty pixels apart.
        subtitle={step === 'done' ? undefined : HEADINGS[step].subtitle}
        compact={step === 'done'}
        backLabel={step === 'otp' ? t(sentTo?.phone ? 'auth.changeNumber' : 'auth.changeAddress')
          : step === 'contact' ? t('auth.persona.change')
          : undefined}
        onBack={step === 'otp' ? () => { setStep('contact'); setCode(''); setCodeError(''); setNumberTaken(false) }
          : step === 'contact' ? () => setStep('persona')
          : undefined}
      />

      { step === 'persona' && (
        <div className="flex flex-col gap-5">
          <PersonaPicker value={persona} onChange={setPersona} />
          <Button onClick={handlePersonaContinue} isActive={!!persona} width="full">
            {t('common.continue')}
          </Button>
          { !resumeTo && (
            <p className="text-center text-[13px] font-medium text-muted-foreground">
              {t('auth.register.hasAccount')}{' '}
              <button type="button" onClick={routeToLogin} className="cursor-pointer font-semibold text-foreground transition-colors hover:text-muted-foreground">
                {t('auth.register.signIn')}
              </button>
            </p>
          ) }
        </div>
      ) }

      { step === 'block' && (
        <FirstBlock
          persona={persona ?? 'person'}
          session={session as Session | null}
          onPreview={setFreshTile}
          onFinish={() => setStep('done')}
        />
      ) }

      { step === 'contact' && (
        <div className="flex flex-col gap-5">
          <GoogleButton onClick={registerWithGoogle} isLoading={googleLoading} />

          <AuthDivider />

          <div className="flex flex-col gap-3.5">
            <Input
              value={identity}
              label={t('auth.identity')}
              // Text rather than email: an email field refuses digits on some
              // phone keyboards and flags a number as malformed.
              type="text"
              autoComplete="username"
              autoCapitalize="none"
              spellCheck={false}
              placeholder={t('auth.identityPlaceholder')}
              onChange={(event: any) => { setIdentity(event.target.value); setIdentityError('') }}
              onKeyDown={(event: any) => { if(event.key === 'Enter') handleSendCode(event) }}
              description={
                readContact(identity)?.phone ? t('auth.smsHint')
                  : readContact(identity)?.email ? t('auth.channelHint', { channel: t('auth.channelEmail') })
                  : ''
              }
              errorText={identityError}
              required
              autoFocus
              container="!max-w-none"
            />
            <Button onClick={handleSendCode} isLoading={loading} width="full" loadingMessage={t('auth.sending')}>
              {t('common.continue')}
            </Button>
          </div>

          <p className="text-center text-xs font-medium leading-relaxed text-muted-foreground">
            {t('auth.register.termsPrefix')}{' '}
            <a href={`${process.env.NEXT_PUBLIC_LANDING_URL}/terms`} target="_blank" rel="noreferrer" className="font-semibold text-foreground">
              {t('auth.register.termsLink')}
            </a>
            {t('auth.register.termsSuffix')}
          </p>

          <p className="text-center text-[13px] font-medium text-muted-foreground">
            {t('auth.register.hasAccount')}{' '}
            <button type="button" onClick={routeToLogin} className="cursor-pointer font-semibold text-foreground transition-colors hover:text-muted-foreground">
              {t('auth.register.signIn')}
            </button>
          </p>
        </div>
      ) }

      { step === 'otp' && (
        <div className="flex flex-col gap-5">
          <OtpField value={code} onChange={setCode} onComplete={handleVerify} label={t('auth.otp.heading')} />
          { codeError && <p className="text-[13px] font-medium text-destructive">{codeError}</p> }
          { numberTaken && sentTo?.phone && (
            <Button
              variant="outline"
              width="full"
              onClick={() => {
                const query = new URLSearchParams({ phone: sentTo.phone })
                if(callbackUrl) query.set('callbackUrl', callbackUrl)
                router.push(`/login?${query}`)
              }}
            >
              {t('auth.otp.signInExisting')}
            </Button>
          ) }
          <div className="flex flex-col gap-2.5">
            <Button onClick={() => handleVerify(code)} isLoading={loading} width="full" loadingMessage={t('auth.verifying')}>
              {t('auth.verify')}
            </Button>
            <Button onClick={handleResend} isLoading={resendLoading} icon={RotateCcw} variant="ghost" width="full" loadingMessage={t('auth.sending')}>
              {t('auth.resend')}
            </Button>
          </div>
        </div>
      ) }

      { step === 'password' && (
        <div className="flex flex-col gap-4">
          <PasswordPair
            password={password}
            confirm={confirm}
            onPasswordChange={setPassword}
            onConfirmChange={setConfirm}
            onSubmit={handlePasswordSubmit}
            autoFocus
          />
          <Button
            onClick={handlePasswordSubmit}
            isActive={isPasswordPairReady(password, confirm)}
            isLoading={loading}
            width="full"
            loadingMessage={t('auth.reset.saving')}
          >
            {t('common.continue')}
          </Button>
        </div>
      ) }

      { step === 'username' && (
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            {/* The handle is the product — fuse.mn/{handle} is what gets shared —
                so it is typed at display size rather than in an ordinary field,
                and it gets the whole row. Status used to sit beside it, which
                cost the field width exactly when it needed it most: the longest
                messages are the failures, so the field shrank as the message
                grew, and a keystroke that flipped the verdict re-flowed the
                text being typed. */}
            <label className="flex cursor-text items-baseline justify-center gap-px pb-1.5 pt-1 lg:justify-start">
              <span className="shrink-0 font-gilroy text-lg font-medium text-muted-foreground sm:text-xl">fuse.mn/</span>
              {/* Below lg the field is sized by its own content so the prefix and
                  the handle centre as one unit. A flex-1 input would stretch to
                  the container and centre only its text, leaving `fuse.mn/`
                  stranded at the left edge with a gap after it. The mirror span
                  carries the same font, so the track it sizes is exact and needs
                  no measuring. From lg the input takes the row as before. */}
              <span className="grid max-w-full min-w-0 overflow-hidden lg:flex-1">
                <span
                  aria-hidden="true"
                  className="invisible col-start-1 row-start-1 whitespace-pre font-gilroy text-2xl font-semibold tracking-tight sm:text-[28px] lg:hidden"
                >
                  {`${username || t('auth.username.placeholder')} `}
                </span>
                <input
                  value={username}
                  onChange={(event) => handleUsernameChange(event.target.value)}
                  onFocus={() => setHandleFocused(true)}
                  onBlur={() => setHandleFocused(false)}
                  onKeyDown={(event) => { if(event.key === 'Enter') handleUsernameSubmit() }}
                  placeholder={t('auth.username.placeholder')}
                  spellCheck={false}
                  autoCapitalize="none"
                  autoFocus
                  aria-label={t('auth.username.heading')}
                  aria-describedby="handle-status"
                  className="col-start-1 row-start-1 w-full min-w-0 border-none bg-transparent p-0 font-gilroy text-2xl font-semibold tracking-tight text-foreground caret-orange-500 outline-none shadow-none focus:border-transparent focus:shadow-none focus:ring-0 placeholder:text-muted-foreground sm:text-[28px]"
                />
              </span>
            </label>

            {/* Height is reserved whether or not there is anything to say, so
                the submit button underneath never moves as the verdict lands. */}
            <div id="handle-status" role="status" aria-live="polite" className="flex min-h-7 items-center justify-center gap-2.5 lg:justify-start">
              { !!username && (
                <>
                  <span
                    className={`flex min-w-0 items-center gap-1.5 rounded-full px-2.5 py-1.5 font-gilroy text-xs font-semibold ${
                      nameChecking ? 'bg-muted text-muted-foreground'
                        : nameFree ? 'bg-success-foreground text-success'
                        : 'bg-danger-foreground text-danger'
                    }`}
                  >
                    { nameChecking ? <Loader2 className="size-3.5 shrink-0 animate-spin" aria-hidden="true" />
                      : nameFree ? <Check className="size-3.5 shrink-0" aria-hidden="true" />
                      : <X className="size-3.5 shrink-0" aria-hidden="true" /> }
                    <span className="truncate">{handleStatus}</span>
                  </span>
                  <span className="shrink-0 text-[11px] font-medium tabular-nums text-muted-foreground lg:ml-auto">
                    {t('auth.username.counter', { count: username.length, max: HANDLE_MAX })}
                  </span>
                </>
              ) }
            </div>
          </div>

          {/* Hidden once the answer is in and empty — a name that transliterates
              to nothing claimable has nothing to offer, and an empty labelled
              panel reads as something having failed. */}
          { (suggestLoading || !!suggestions.length) && (
            <div className="flex flex-col gap-2 text-center lg:text-left">
              <span className="text-[11px] font-medium text-muted-foreground">{t('auth.username.suggestions')}</span>
              <div className="flex flex-wrap justify-center gap-2 lg:justify-start" aria-busy={suggestLoading}>
                { suggestLoading
                  ? SUGGESTION_SKELETONS.map(width => (
                    <Skeleton key={width} className={`h-8 rounded-full ${width}`} />
                  ))
                  : suggestions.map(value => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => handlePickSuggestion(value)}
                      aria-pressed={username === value}
                      className={`cursor-pointer rounded-full border px-3 py-1.5 font-gilroy text-[13px] font-semibold transition-colors ${
                        username === value
                          ? 'border-transparent bg-secondary text-secondary-foreground'
                          : 'border-border text-muted-foreground hover:border-muted-foreground hover:text-foreground'
                      }`}
                    >
                      {value}
                    </button>
                  )) }
              </div>
            </div>
          ) }

          <Button
            onClick={handleUsernameSubmit}
            isActive={nameFree && !nameChecking}
            isLoading={loading}
            width="full"
            loadingMessage={t('auth.reset.saving')}
          >
            {t('auth.username.submit')}
          </Button>
        </div>
      ) }

      { step === 'profile' && (
        <div className="flex flex-col gap-6">
          <div className="flex items-center justify-center gap-4 lg:justify-start">
            <input
              ref={fileRef}
              type="file"
              accept={AVATAR_TYPES}
              onChange={handleAvatarPick}
              className="hidden"
              tabIndex={-1}
              aria-hidden="true"
            />
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={avatarUploading}
              aria-label={t('auth.profile.avatar')}
              // The limit on hover, since a round picker has nowhere to put a
              // hint line without inventing a caption for it.
              title={t('dashboard.config.imageHint', { max: AVATAR_MAX_MB })}
              className={`flex size-24 shrink-0 cursor-pointer items-center justify-center overflow-hidden rounded-full transition-colors disabled:cursor-wait ${
                avatar ? 'border border-border'
                  : chosen ? `${chosen.className} border border-border`
                  : 'border border-dashed border-input text-muted-foreground hover:border-muted-foreground hover:text-foreground'
              }`}
            >
              { avatarUploading
                ? <Loader2 className="size-6 animate-spin" aria-hidden="true" />
                : avatar
                  // Not next/image: the bucket host is not in next.config's
                  // remotePatterns, and this is a one-off preview, not a rendered
                  // page asset.
                  ? <img src={formatImageURL(avatar)} alt="" className="size-full object-cover" />
                  : chosen
                    ? <span className="font-gilroy text-3xl font-bold">{initialsOf(fullName) || 'F'}</span>
                    : <ImagePlus className="size-6" aria-hidden="true" /> }
            </button>
            <div className="flex min-w-0 flex-col gap-2">
              <div className="flex items-center gap-2">
                <span className="font-gilroy text-sm font-semibold text-foreground">{t('auth.profile.avatar')}</span>
                <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium text-muted-foreground">{t('auth.profile.optional')}</span>
              </div>
              <span className="text-xs font-medium leading-snug text-muted-foreground">{t('auth.profile.avatarHint', { max: AVATAR_MAX_MB })}</span>
            </div>
          </div>

          <div className="flex flex-col gap-2.5 text-center lg:text-left">
            <span className="text-[11px] font-medium text-muted-foreground">{t('auth.profile.presets')}</span>
            <div className="flex flex-wrap items-center justify-center gap-2.5 lg:justify-start">
              { AVATARS.map(option => (
                <button
                  key={option.key}
                  type="button"
                  // A colour and a photo are alternatives, not layers: picking
                  // one drops the other.
                  onClick={() => { setPreset(option.key); setAvatar(null) }}
                  aria-pressed={preset === option.key}
                  className={`flex size-10 shrink-0 cursor-pointer items-center justify-center rounded-full border border-border font-gilroy text-[13px] font-bold transition-shadow ${option.className} ${
                    preset === option.key ? 'ring-2 ring-ring' : ''
                  }`}
                >
                  {initialsOf(fullName) || 'F'}
                </button>
              )) }
              { (preset || avatar) && (
                <button
                  type="button"
                  onClick={() => { setPreset(null); setAvatar(null) }}
                  title={t('auth.profile.remove')}
                  className="flex size-10 shrink-0 cursor-pointer items-center justify-center rounded-full border border-dashed border-input text-muted-foreground transition-colors hover:text-foreground"
                >
                  <X className="size-4" aria-hidden="true" />
                </button>
              ) }
            </div>
          </div>

          <label className="flex cursor-text flex-col gap-1.5 text-center lg:text-left">
            <span className="text-[11px] font-medium text-muted-foreground">{t('auth.profile.fullName')}</span>
            <input
              value={fullName}
              onChange={(event) => setFullName(event.target.value)}
              placeholder={t('auth.profile.fullNamePlaceholder')}
              autoFocus
              className="border-none bg-transparent p-0 font-gilroy text-2xl font-semibold tracking-tight text-foreground caret-orange-500 outline-none shadow-none focus:border-transparent focus:shadow-none focus:ring-0 placeholder:text-muted-foreground sm:text-[28px]"
            />
          </label>

          {/* Collected here rather than at the contact step, because that step
              takes either an email or a phone and turning it into "both" would
              add a field to every sign-up including the Google one, which
              cannot supply a phone at all. Here it is one field on a screen
              somebody already has to fill in, and it is what lets the account
              register as a QPay merchant later. Skipped when the contact step
              was the phone. */}
          { needPhone && (
            <label className="flex cursor-text flex-col gap-1.5">
              <span className="text-[11px] font-medium text-muted-foreground">{t('auth.profile.phone')}</span>
              <input
                value={phone}
                // Digits as they arrive. A pasted number with spaces or a +976
                // should work rather than be rejected for punctuation.
                onChange={(event) => setPhone(event.target.value.replace(/\D/g, '').replace(/^976/, '').slice(0, 8))}
                inputMode="numeric"
                autoComplete="tel"
                placeholder={t('auth.profile.phonePlaceholder')}
                className="border-none bg-transparent p-0 font-gilroy text-2xl font-semibold tabular-nums tracking-tight text-foreground caret-orange-500 outline-none shadow-none focus:border-transparent focus:shadow-none focus:ring-0 placeholder:text-muted-foreground sm:text-[28px]"
              />
              <span className="text-[11px] font-medium leading-snug text-muted-foreground">
                {t('auth.profile.phoneHint')}
              </span>
            </label>
          ) }

          <label className="flex cursor-text flex-col gap-1.5">
            <span className="flex items-center gap-2">
              <span className="text-[11px] font-medium text-muted-foreground">{t('auth.profile.bio')}</span>
              <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium text-muted-foreground">{t('auth.profile.optional')}</span>
              <span className="ml-auto text-[11px] font-medium tabular-nums text-muted-foreground">
                {t('auth.username.counter', { count: bio.length, max: BIO_MAX })}
              </span>
            </span>
            <textarea
              value={bio}
              onChange={(event) => setBio(event.target.value.slice(0, BIO_MAX))}
              rows={3}
              placeholder={t('auth.profile.bioPlaceholder')}
              className="resize-none border-none bg-transparent p-0 text-base font-medium leading-relaxed text-foreground caret-orange-500 outline-none shadow-none focus:border-transparent focus:shadow-none focus:ring-0 placeholder:text-muted-foreground"
            />
          </label>

          <Button
            onClick={handleFinish}
            isActive={fullName.trim().length > 1 && (!needPhone || phone.length === 8)}
            isLoading={loading}
            width="full"
            loadingMessage={t('auth.profile.creating')}
          >
            {t('auth.profile.submit')}
          </Button>
        </div>
      ) }

      { step === 'done' && (
        <div className="flex flex-col gap-7">
          <Celebrate />

          {/* The card is the page in miniature — face, name, address — read top
              to bottom in that order, because that is the order someone landing
              on fuse.mn/{handle} reads it too. Below lg the aside is hidden,
              which makes this the only place the payoff appears at all.

              The address stays the largest thing on it: what someone came here
              to get is a URL they can paste into a bio, and the name above it
              is context rather than the prize. */}
          <div className="animate-rise flex flex-col items-center gap-4 rounded-3xl border border-border bg-card p-6 text-center shadow-md">
            <div className={`flex size-16 shrink-0 items-center justify-center overflow-hidden rounded-full border border-border font-gilroy text-lg font-bold ${chosen?.className || 'bg-muted text-muted-foreground'}`}>
              { avatar
                ? <img src={formatImageURL(avatar)} alt="" className="size-full object-cover" />
                : initialsOf(fullName) || 'F' }
            </div>

            <div className="flex w-full min-w-0 flex-col gap-2">
              { !!fullName.trim() && (
                <p className="font-gilroy text-[15px] font-semibold leading-tight text-muted-foreground">
                  {fullName.trim()}
                </p>
              ) }
              {/* `anywhere` rather than `break-all`: a handle that fits stays on
                  one line, and only one long enough to overflow is broken. The
                  clamp is sized so a 30-character handle — the column maximum —
                  still lands inside the card on a narrow phone. */}
              <p className="w-full font-gilroy text-[clamp(18px,4.8vw,26px)] font-bold leading-tight tracking-tight text-foreground [overflow-wrap:anywhere]">
                <span className="text-muted-foreground">{PAGE_HOST}/</span>{username}
              </p>
            </div>

            <button
              type="button"
              onClick={handleCopyLink}
              className="flex h-10 w-full cursor-pointer items-center justify-center gap-2 rounded-xl border border-border bg-secondary font-gilroy text-[13px] font-semibold text-secondary-foreground transition-colors hover:bg-accent"
            >
              { copied
                ? <><Check className="size-4 text-success" aria-hidden="true" />{t('auth.done.copied')}</>
                : <><Copy className="size-4" aria-hidden="true" />{t('auth.done.copy')}</> }
            </button>
          </div>

          <div className="animate-rise flex flex-col gap-3" style={{ animationDelay: '90ms' }}>
            {/* The account's own page, not the home page and not callbackUrl.
                The label says "go to your page", and the whole wizard exists to
                produce this one URL — landing anywhere else reads as the app
                having lost track of what was just built. */}
            <Button onClick={() => router.push(`/${username}`)} width="full">
              {t('auth.done.enter')}
            </Button>
            <p className="text-center text-[13px] font-medium leading-relaxed text-muted-foreground">
              {t('auth.done.shareHint')}
            </p>
          </div>
        </div>
      ) }
    </AuthShell>
  )
}
